import { Component, Input, OnInit } from '@angular/core';
import { catchError, forkJoin, map, Observable, of, switchMap, tap } from 'rxjs';
import { LangProductMapper } from 'src/app/col/utils/lang-mappers';
import { Dates } from 'src/app/utils/calendar-utils';
import { NativeServiceApiProvider } from 'src/app/services/native-api-provider.service';
import { Pageable } from 'src/app/utils/pageable';
import { ColNativeV3Api } from '../../../services/api/v3/col-native-v3-api';
import * as LessonsV3 from '../../../model/v3/lessons';

type LessonFlagLike = LessonsV3.ApiLessonFlag | LessonsV3.LessonFlagType;
type LessonWithOptionalFields = LessonsV3.LFViewBase & Partial<{
  id: number;
  status: LessonsV3.LessonStatus;
  flags: LessonFlagLike[];
}>;

class ReportData {
  complete = 0;
  studentNoShow = 0;
  invalidatedByStudent = 0;
  invalidatedByTeacher = 0;
  cancelled = 0;
}

@Component({
    selector: 'app-teacher-quick-lesson-counter',
    templateUrl: './teacher-quick-lesson-counter.component.html',
    styleUrls: ['./teacher-quick-lesson-counter.component.scss'],
    standalone: false
})
export class TeacherQuickLessonCounterComponent implements OnInit {
  private readonly reportStatuses: LessonsV3.LessonStatus[] = [
    LessonsV3.LessonStatus.Complete,
    LessonsV3.LessonStatus.Cancel,
    LessonsV3.LessonStatus.InvalidatedByStudent,
    LessonsV3.LessonStatus.InvalidatedByTeacher,
  ];

  langCodes = LangProductMapper.activeLangCodes;
  productCode: string;
  dateFrom: Date;
  dateTo: Date;
  reportData: ReportData;
  preselectedPeriod = '0';
  private _teacherId: number

  @Input()
  set teacherId(teacherId: number) {
    this._teacherId = teacherId;
  }

  constructor(private provider: NativeServiceApiProvider) { }

  ngOnInit(): void {
  }

  getLangName(langCode: string) {
    return LangProductMapper.mapLangCodeToLangName(langCode);
  }

  private mapResults(
    lessons: LessonsV3.LFViewBase[],
    fallbackFlagsByLessonId: Map<number, LessonsV3.ApiLessonFlag[]>
  ): ReportData {
    const res = new ReportData();
    for (const lesson of lessons) {
      const status = this.extractLessonStatus(lesson);
      switch (status) {
        case LessonsV3.LessonStatus.Complete:
          if (this.hasStudentNoShowFlag(lesson, fallbackFlagsByLessonId)) {
            res.studentNoShow += 1;
          } else {
            res.complete += 1;
          }
          break;
        case LessonsV3.LessonStatus.Cancel:
          res.cancelled += 1;
          break;
        case LessonsV3.LessonStatus.InvalidatedByTeacher:
          res.invalidatedByTeacher += 1;
          break;
        case LessonsV3.LessonStatus.InvalidatedByStudent:
          res.invalidatedByStudent += 1;
          break;
      }
    }
    return res;
  }

  private hasStudentNoShowFlag(
    lesson: LessonsV3.LFViewBase,
    fallbackFlagsByLessonId: Map<number, LessonsV3.ApiLessonFlag[]>
  ): boolean {
    const directNoShow = this.extractStudentNotShowFromLesson(lesson);
    if (directNoShow != null) {
      return directNoShow;
    }

    const lessonId = this.extractLessonId(lesson);
    if (lessonId == null) {
      return false;
    }

    const fallbackFlags = fallbackFlagsByLessonId.get(lessonId) ?? [];
    return fallbackFlags.some(
      (flag) => flag.flagType === LessonsV3.LessonFlagType.StudentNotShow
    );
  }

  private extractLessonStatus(lesson: LessonsV3.LFViewBase): LessonsV3.LessonStatus | null {
    const candidate = (lesson as LessonWithOptionalFields).status;
    return candidate ?? null;
  }

  private extractLessonId(lesson: LessonsV3.LFViewBase): number | null {
    const candidate = (lesson as LessonWithOptionalFields).id;
    return typeof candidate === 'number' ? candidate : null;
  }

  private extractStudentNotShowFromLesson(lesson: LessonsV3.LFViewBase): boolean | null {
    const flags = (lesson as LessonWithOptionalFields).flags;
    if (!flags) {
      return null;
    }
    return flags.some((flag) => {
      if (typeof flag === 'string') {
        return flag === LessonsV3.LessonFlagType.StudentNotShow;
      }
      return flag.flagType === LessonsV3.LessonFlagType.StudentNotShow;
    });
  }

  private loadAllLessons(
    api: ColNativeV3Api,
    pageable: Pageable,
    acc: LessonsV3.LFViewBase[]
  ): Observable<LessonsV3.LFViewBase[]> {
    return api
      .findLessons(
        LessonsV3.LessonCategory.Col,
        null,
        null,
        null,
        null,
        this._teacherId,
        null,
        null,
        this.productCode || null,
        this.reportStatuses,
        this.dateFrom,
        this.dateTo,
        pageable
      )
      .pipe(
        switchMap((page) => {
          const current = page?.content ?? [];
          const merged = acc.concat(current);
          if (page?.last || current.length === 0) {
            return of(merged);
          }
          return this.loadAllLessons(api, pageable.next(), merged);
        })
      );
  }

  private loadMissingFlags(
    api: ColNativeV3Api,
    lessons: LessonsV3.LFViewBase[]
  ): Observable<Map<number, LessonsV3.ApiLessonFlag[]>> {
    const lessonIdsMissingFlags = lessons
      .filter((lesson) => this.extractStudentNotShowFromLesson(lesson) == null)
      .map((lesson) => this.extractLessonId(lesson))
      .filter((id): id is number => id != null);

    if (lessonIdsMissingFlags.length === 0) {
      return of(new Map<number, LessonsV3.ApiLessonFlag[]>());
    }

    return forkJoin(
      lessonIdsMissingFlags.map((lessonId) =>
        api.getLessonFlags(lessonId).pipe(
          map((flags) => [lessonId, flags] as [number, LessonsV3.ApiLessonFlag[]]),
          catchError(() => of([lessonId, [] as LessonsV3.ApiLessonFlag[]] as [number, LessonsV3.ApiLessonFlag[]]))
        )
      )
    ).pipe(map((entries) => new Map<number, LessonsV3.ApiLessonFlag[]>(entries)));
  }

  private loadReportData(api: ColNativeV3Api): Observable<ReportData> {
    return this.loadAllLessons(
      api,
      Pageable.of(0, 200, ['metric.details.plannedStartDate,ASC']),
      []
    ).pipe(
      switchMap((lessons) =>
        forkJoin({
          lessons: of(lessons),
          fallbackFlagsByLessonId: this.loadMissingFlags(api, lessons),
        })
      ),
      map(({lessons, fallbackFlagsByLessonId}) =>
        this.mapResults(lessons, fallbackFlagsByLessonId)
      )
    );
  }

  calculateDates(): void {
    const period = Number(this.preselectedPeriod);
    this.dateFrom = Dates.monthStartDate(period);
    this.dateTo = period === 0 ? new Date() : Dates.monthFinishDate(period);
  }

  updateReport(): void {
    this.calculateDates();
    if (!this._teacherId) {
      return;
    }
    this.provider
      .colV3()
      .pipe(
        switchMap((api) => this.loadReportData(api)),
        tap((reportData) => (this.reportData = reportData))
      )
      .subscribe();
  }


}
